import { PERMISSION_CODES, PERMISSIONS, type PermissionCode } from "@seagames/contracts";
import { type ColumnDef, DataTable, ListHeader, useColumnVisibility } from "@seagames/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { requireAdminRole } from "../../lib/auth/guards";
import { permissionAreaLabel, permissionDescription, permissionLabel } from "../../lib/user-groups/labels";
import { roleLabel } from "../../lib/users/labels";
import { Tag } from "../../components/users/ui";

/**
 * Users › Modules — the permission catalogue, read-only. It is a constant in
 * @seagames/contracts (seven codes; adding one is a migration), so there is
 * nothing to fetch and nothing to page: the rows are the catalogue itself.
 */
export const Route = createFileRoute("/_app/users/modules")({
	beforeLoad: ({ context }) => requireAdminRole(context.session),
	component: ModulesPage,
});

interface Row {
	code: PermissionCode;
}
const ROWS: readonly Row[] = PERMISSION_CODES.map((code) => ({ code }));

function ModulesPage() {
	const { t } = useTranslation("userGroups");
	const { t: tUsers } = useTranslation(["users", "common"]);
	const columnVisibility = useColumnVisibility("permission-catalogue");

	const columns: ColumnDef<Row>[] = [
		{
			key: "permission",
			header: t("modules.permission"),
			lockVisible: true,
			minWidth: "12rem",
			cell: (r) => <span className="text-[0.84375rem] font-semibold text-gms-navy-ink">{permissionLabel(t, r.code)}</span>,
		},
		{
			key: "code",
			header: t("modules.code"),
			minWidth: "11rem",
			cell: (r) => <span className="font-mono text-[0.75rem] text-gms-ink-2">{r.code}</span>,
		},
		{
			key: "area",
			header: t("modules.area"),
			width: "8rem",
			cell: (r) => <span>{permissionAreaLabel(t, PERMISSIONS[r.code].area)}</span>,
		},
		{
			key: "description",
			header: t("modules.description"),
			minWidth: "18rem",
			cell: (r) => <span className="text-gms-ink-2">{permissionDescription(t, r.code)}</span>,
		},
		{
			key: "roles",
			header: t("modules.roles"),
			minWidth: "11rem",
			cell: (r) => (
				<div className="flex flex-wrap gap-[0.25rem]">
					{PERMISSIONS[r.code].roles.map((role) => (
						<Tag key={role}>{roleLabel(tUsers, role)}</Tag>
					))}
				</div>
			),
		},
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader title={t("modules.title")} count={t("modules.summary", { count: ROWS.length, total: ROWS.length })} description={t("modules.intro")} />
			<DataTable<Row> variant="list" columns={columns} rows={ROWS} rowKey={(r) => r.code} columnVisibility={columnVisibility} />
		</div>
	);
}
